import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '@headlessui/react';
import { Search, Copy, UserCheck, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../components/ui/Card';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { saveAccountsCache } from '../lib/impersonation';

type Facility = {
  type: string;
  facility_name: string | null;
  location_id: string | null;
  status: string;
};

type Account = {
  user_id: string;
  email: string | null;
  last_sign_in_at: string | null;
  facilities: Facility[];
};

async function fetchAccounts(q: string, accessToken: string): Promise<{ accounts: Account[]; total: number }> {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('limit', '50');
  const res = await fetch(`/.netlify/functions/admin-list-accounts?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.error || `Failed to load accounts (${res.status})`);
  }
  const accounts = json.accounts || [];
  if (!q && accounts.length > 0) {
    saveAccountsCache(accounts);
  }
  return { accounts, total: json.total ?? 0 };
}

function facilityLabel(facilities: Facility[]): string {
  if (!facilities?.length) return '—';
  return facilities
    .map((f) => {
      const name = f.facility_name || f.type;
      return f.location_id ? `${name} · ${f.location_id}` : name;
    })
    .join(', ');
}

function statusPills(facilities: Facility[]) {
  const byType = new Map(facilities.map((f) => [f.type, f.status]));
  const types = ['4d', 'zonedm'];
  return types.map((t) => {
    const status = byType.get(t);
    if (!status) {
      return (
        <span key={t} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500 mr-1">
          {t === 'zonedm' ? 'GHL' : '4D'} —
        </span>
      );
    }
    const active = status === 'active';
    return (
      <span
        key={t}
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs mr-1 ${
          active ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}
      >
        {t === 'zonedm' ? 'GHL' : '4D'} {active ? 'active' : status}
      </span>
    );
  });
}

export default function AdminAccountsPage() {
  const { startImpersonation } = useAuth();
  const [searchInput, setSearchInput] = React.useState('');
  const [q, setQ] = React.useState('');
  const [selected, setSelected] = React.useState<Account | null>(null);
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [modalError, setModalError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-accounts', q],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      return fetchAccounts(q, token);
    },
  });

  const accounts = data?.accounts || [];

  const openModal = (account: Account) => {
    setSelected(account);
    setReason('');
    setModalError(null);
  };

  const closeModal = () => {
    if (submitting) return;
    setSelected(null);
    setReason('');
    setModalError(null);
  };

  const handleImpersonate = async () => {
    if (!selected) return;
    const trimmed = reason.trim();
    if (trimmed.length < 10) {
      setModalError('Add a short reason (10+ characters)');
      return;
    }
    setSubmitting(true);
    setModalError(null);
    try {
      await startImpersonation({
        target_user_id: selected.user_id,
        target_email: selected.email || selected.user_id,
        reason: trimmed,
      });
    } catch (e: any) {
      setModalError(e?.message || 'Failed to open as customer');
      setSubmitting(false);
    }
  };

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success('User ID copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Accounts</h1>
        <p className="mt-1 text-sm text-gray-500">
          Find a clinic account and open their dashboard to set up integrations or fix mappings.
        </p>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search email or facility name..."
          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
          aria-label="Search accounts"
        />
      </div>

      <Card>
        {isLoading || isFetching ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-red-600 mb-3">
              {(error as Error)?.message || "Couldn't load accounts."}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              Retry
            </button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-600 mb-3">
              No accounts match. Try another email or facility name.
            </p>
            {q && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setQ('');
                }}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((a) => (
                  <tr key={a.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">
                      {a.email || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {facilityLabel(a.facilities)}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">{statusPills(a.facilities)}</td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openModal(a)}
                        className="inline-flex items-center min-h-[44px] px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 mr-2"
                      >
                        <UserCheck className="w-4 h-4 mr-1.5" />
                        Open as customer
                      </button>
                      <button
                        type="button"
                        onClick={() => copyId(a.user_id)}
                        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 min-h-[44px] px-2"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy ID
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {typeof data?.total === 'number' && (
              <p className="px-4 py-2 text-xs text-gray-500 border-t">
                Showing {accounts.length} of {data.total} match{data.total === 1 ? '' : 'es'}
              </p>
            )}
          </div>
        )}
      </Card>

      <Dialog open={!!selected} onClose={closeModal} className="relative z-[60]">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Open dashboard as {selected?.email || 'customer'}?
              </Dialog.Title>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              You&apos;ll see and can change their integrations and mappings. This is audited.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Why are you opening this account?
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ottawa pipeline stage stuck after reschedule"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-gray-500">{reason.trim().length}/10 minimum</p>
            {modalError && <p className="mt-2 text-sm text-red-600">{modalError}</p>}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md border border-gray-300 min-h-[44px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImpersonate}
                disabled={submitting || reason.trim().length < 10}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 min-h-[44px]"
              >
                {submitting ? 'Opening…' : 'Open as customer'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
